import { useRecoilState } from 'recoil';
import { useHistory } from 'react-router';

import { Box, Divider, Heading, Modal } from '@zillow/constellation-10';

import { StyledZDocsEditor } from '../../constants/styleConstants';
import LeaseExtension from '../../models/LeaseExtension';
import LeaseActionButtonGroup from '../../components/LeaseActions/LeaseActionButtonGroup';
import type { LeaseAction } from '../../models';
import {
  useLeaseSummaryFromOuidQuery,
  useMarkAsSignedFromOuidMutation,
  usePreviewUrlFromOuidQuery,
} from '../../utils/queries';
import { SigningCompleteModal } from '../../components/LeaseRenewal/Colandlord/SigningCompleteModal';
import { isSigningCompleteModalOpenState } from '../../../atoms';
import NoAccessLeaseRenewalAlert from '../../components/Widgets/NoAccessLeaseRenewalAlert';
import { useLeasingUrlParams } from '../../hooks';

export default function RenewalPDFViewer() {
  const [, setIsSigningCompleteModalOpen] = useRecoilState(isSigningCompleteModalOpenState);

  const history = useHistory();
  const queryParameters = new URLSearchParams(window.location.search);
  const ouid = queryParameters.get('ouid');
  const { leaseId, renewalId } = useLeasingUrlParams();

  const previewUrlFromOuidQuery = usePreviewUrlFromOuidQuery(ouid, !!ouid);
  const leaseSummaryFromOuidQuery = useLeaseSummaryFromOuidQuery(ouid, !!ouid);
  const markAsSignedFromOuidMutation = useMarkAsSignedFromOuidMutation();

  function onClose() {
    history.push(LeaseExtension.getSummaryPath(leaseId, renewalId));
  }

  async function onSigningComplete() {
    await markAsSignedFromOuidMutation.mutateAsync({ getParams: { ouid } });
    setIsSigningCompleteModalOpen(true);
  }

  return (
    <>
      <Modal.Root
        size="fullScreen"
        open
        onOpenChange={(open) => {
          if (!open) {
            onClose();
          }
        }}
        useDismissProps={{ escapeKey: false, outsidePress: false }}
      >
        <Modal.Portal>
          <Modal.Backdrop>
            <Modal.Content>
              <Modal.Header>
                <Heading level={1}>Your lease renewal agreement</Heading>
              </Modal.Header>
              <Divider />
              <Modal.Body css={{ padding: '0 !important' }}>
                {(previewUrlFromOuidQuery.isError || !ouid) && (
                  <Box css={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Box css={{ margin: 'tight' }}>
                      <NoAccessLeaseRenewalAlert />
                    </Box>
                  </Box>
                )}
                {previewUrlFromOuidQuery.data?.url && (
                  <StyledZDocsEditor onDocumentSaved={onSigningComplete} url={previewUrlFromOuidQuery.data.url} />
                )}
              </Modal.Body>
              <Divider />
              <Modal.Footer>
                {leaseSummaryFromOuidQuery.data?.documentActions?.length > 0 ? (
                  <LeaseActionButtonGroup
                    leaseActions={leaseSummaryFromOuidQuery.data?.documentActions as Array<LeaseAction>}
                    isTop={false}
                  />
                ) : null}
              </Modal.Footer>
              <Modal.CloseButton />
            </Modal.Content>
          </Modal.Backdrop>
        </Modal.Portal>
      </Modal.Root>
      <SigningCompleteModal />
    </>
  );
}
